import React, { useEffect, useState } from 'react';
import { Link, useNavigate, Outlet, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Book, FileText, Tags, Users,
  Settings, LogOut, Menu, X, ChevronRight,
  TrendingUp, Eye, UserCheck, Download, File
} from 'lucide-react';
import { useAuthStore } from '../../hooks/useStore';
import { novelAPI, authAPI } from '../../services/api';
import './Admin.css';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Đang verify auth (chưa có user) → show loading
  if (isLoading && !user) {
    return (
      <div className="admin-loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Không authenticated và không loading → về login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role !== 'admin' && user.role !== 'author') {
    return <Navigate to="/" replace />;
  }

  const isAdmin = user.role === 'admin';

  const handleLogout = () => {
    useAuthStore.getState().logout();
    navigate('/');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/novels', icon: Book, label: 'Novels' },
    { path: '/admin/chapters', icon: FileText, label: 'Chapters' },
    { path: '/admin/genres', icon: Tags, label: 'Genres', adminOnly: true },
    { path: '/admin/crawler', icon: Download, label: 'Crawler', adminOnly: true },
    { path: '/admin/pages', icon: File, label: 'Pages', adminOnly: true },
    { path: '/admin/domains', icon: Settings, label: 'Domains', adminOnly: true },
    { path: '/admin/ads', icon: TrendingUp, label: 'Ads', adminOnly: true },
    { path: '/admin/users', icon: Users, label: 'Users', adminOnly: true },
  ];

  const isActive = (path, exact = false) => exact ? location.pathname === path : location.pathname.startsWith(path);

  const adminOnlyPaths = ['/admin/genres', '/admin/crawler', '/admin/pages', '/admin/domains', '/admin/ads', '/admin/users'];
  if (!isAdmin && adminOnlyPaths.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="admin-logo"><Book size={24} /><span>HotNovel</span></Link>
          <button className="close-sidebar" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            if (item.adminOnly && !isAdmin) return null;
            return (
              <Link key={item.path} to={item.path}
                className={`nav-item ${isActive(item.path, item.exact) ? 'active' : ''}`}
                onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}>
                <item.icon size={20} /><span>{item.label}</span><ChevronRight size={16} className="arrow" />
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user.username?.charAt(0).toUpperCase()}</div>
            <div className="user-details">
              <span className="user-name">{user.username}</span>
              <span className="user-role">{user.role}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}><LogOut size={18} /></button>
        </div>
      </aside>
      <main className="admin-main">
        <header className="admin-header">
          <button className="toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}><Menu size={24} /></button>
          <div className="header-right"><Link to="/" className="back-to-site">Back to Site</Link></div>
        </header>
        <div className="admin-content"><Outlet /></div>
      </main>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export const Dashboard = () => {
  const [stats, setStats] = useState({ novels: 0, chapters: 0, users: 0, views: 0 });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [novelsRes, usersRes] = await Promise.allSettled([
          novelAPI.getAll({ limit: 1 }),
          authAPI.getAllUsers({ limit: 1 }),
        ]);
        setStats({
          novels: novelsRes.status === 'fulfilled' ? (novelsRes.value.pagination?.total ?? novelsRes.value.data?.length ?? 0) : 0,
          chapters: 0,
          users: usersRes.status === 'fulfilled' ? (usersRes.value.pagination?.total ?? usersRes.value.data?.length ?? 0) : 0,
          views: 0,
        });
      } catch (e) { console.error(e); }
      finally { setIsLoading(false); }
    };
    fetchStats();
  }, []);

  const statCards = [
    { icon: Book, label: 'Total Novels', value: isLoading ? '...' : stats.novels.toLocaleString(), color: '#8b5cf6' },
    { icon: FileText, label: 'Total Chapters', value: isLoading ? '...' : stats.chapters.toLocaleString(), color: '#f472b6' },
    { icon: UserCheck, label: 'Users', value: isLoading ? '...' : stats.users.toLocaleString(), color: '#22c55e' },
    { icon: Eye, label: 'Total Views', value: isLoading ? '...' : stats.views.toLocaleString(), color: '#3b82f6' },
  ];

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      <p className="subtitle">System Overview</p>
      <div className="stats-grid">
        {statCards.map((stat, idx) => (
          <div key={idx} className="stat-card" style={{ '--stat-color': stat.color }}>
            <div className="stat-icon"><stat.icon size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/admin/novels/new" className="action-card"><Book size={24} /><span>Add New Novel</span></Link>
          <Link to="/admin/chapters/new" className="action-card"><FileText size={24} /><span>Add New Chapter</span></Link>
          <Link to="/admin/ads/new" className="action-card"><TrendingUp size={24} /><span>Create Ad Unit</span></Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
